import { makeStyles, tokens, Text, CheckboxOnChangeData, OptionOnSelectData } from "@fluentui/react-components";
import { NewformaCheckbox } from "../../../../components/_base/Checkbox";
import { NewformaDatePicker } from "../../../../components/_base/NewformaDatePicker";
import NewformaDropdown, { NewformaDropdownOption } from "../../../../components/_base/NewformaDropdown";
import React, { useMemo } from "react";
import { DateHelper } from "../../../../helpers/DateHelpers";

const ON_DATE_KEY = "on-date";

const useStyles = makeStyles({
    row: {
        display: "flex",
        alignItems: "center",
        gap: "4px",
    },
    label: {
        fontSize: "12px",
        whiteSpace: "nowrap",
        flexShrink: 0,
    },
    dropdown: {
        flexGrow: 1,
        minWidth: 0,
    },
    dateInput: {
        marginTop: "4px",
        marginLeft: tokens.spacingHorizontalS,
    },
});

export interface DateSelectionOutput {
    Checked: boolean;
    SelectedOption: string;
    SelectedDate: Date | undefined;
}

export interface DateSelectionSectionProps {
    testId: string;
    checked: boolean;
    label: string;
    options: NewformaDropdownOption[];
    selectedOption: string;
    optionsPlaceholder: string;
    selectedDate: Date | undefined;
    disabled?: boolean;
    minDate?: Date;
    maxDate?: Date;
    onChange?: (output: DateSelectionOutput) => void;
}

export const DateSelectionSection: React.FC<DateSelectionSectionProps> = (props: DateSelectionSectionProps) => {
    const isDisabled = props.disabled ?? false;

    const styles = useStyles();

    const displayValue = useMemo(() => {
        const selected = props.options.find((o) => o.key === props.selectedOption);
        return selected ? selected.text : "";
    }, [props.options, props.selectedOption]);

    const showDatePicker = useMemo(() => props.checked && props.selectedOption === ON_DATE_KEY, [
        props.checked,
        props.selectedOption,
    ]);

    const tomorrow = useMemo(() => DateHelper.addDays(DateHelper.TodayDate, 1), []);

    const handleCheckedChange = (_ev: any, data: CheckboxOnChangeData) => {
        props.onChange?.({
            Checked: !!data.checked,
            SelectedOption: props.selectedOption,
            SelectedDate: props.selectedDate,
        });
    };

    const handleDropdownChange = (_ev: any, data: OptionOnSelectData) => {
        props.onChange?.({
            Checked: props.checked,
            SelectedOption: data.optionValue || "",
            SelectedDate: props.selectedDate,
        });
    };

    const handleDateChange = (date: Date | null | undefined) => {
        props.onChange?.({
            Checked: props.checked,
            SelectedOption: props.selectedOption,
            SelectedDate: date ?? undefined,
        });
    };

    return (
        <>
            <div className={styles.row}>
                <NewformaCheckbox
                    checked={props.checked}
                    disabled={isDisabled}
                    onChange={handleCheckedChange}
                    dataTestId={`${props.testId}-checkbox`}
                />
                <Text className={styles.label}>{props.label}</Text>
                <div className={styles.dropdown}>
                    <NewformaDropdown
                        data-testid={`${props.testId}-dropdown`}
                        options={props.options}
                        selectedOptions={[props.selectedOption]}
                        value={displayValue}
                        onOptionSelect={handleDropdownChange}
                        disabled={props.checked ? isDisabled : true}
                        placeholder={props.optionsPlaceholder}
                        positioning={{ matchTargetSize: undefined, overflowBoundaryPadding: 8, autoSize: "width" }}
                        listbox={{ style: { maxHeight: "180px" } }}
                    />
                </div>
            </div>
            {showDatePicker && (
                <NewformaDatePicker
                    data-testid={`${props.testId}-date-picker`}
                    className={styles.dateInput}
                    value={props.selectedDate}
                    onSelectDate={handleDateChange}
                    minDate={props.minDate ?? tomorrow}
                    maxDate={props.maxDate}
                    disabled={isDisabled}
                    aria-label={props.label}
                />
            )}
        </>
    );
};
